import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SeoHead from '@/components/feature/SeoHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { novels, categories } from '@/mocks/novels';
import type { Novel } from '@/mocks/novels';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'urban-revenge');
  const [filteredNovels, setFilteredNovels] = useState<Novel[]>([]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    setFilteredNovels(novels.filter((n) => n.categorySlug === activeSlug));
  }, [activeSlug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 都市美艳后宫`,
    description: currentCategory?.description || '都市爽文分类浏览',
    url: window.location.href,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredNovels.map((novel, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: novel.title,
          author: { '@type': 'Person', name: novel.author },
          url: `${window.location.origin}/article/${novel.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SeoHead
        title={`${currentCategory?.name || '分类'} - 都市美艳后宫 | 都市爽文推荐`}
        description={currentCategory?.description || '海量都市爽文分类浏览，找到你的最爱'}
        keywords={`${currentCategory?.name || '都市爽文'},都市美艳后宫,都市后宮爽文,美艳女主角`}
        canonicalUrl={window.location.href}
        ogType="website"
        schema={categorySchema}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-18">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/urban-revenge' },
                { label: currentCategory?.name || '' },
              ]}
            />

            <div className="px-4 md:px-6 py-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
            </div>

            <div className="px-4 md:px-6 pb-6">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-4 md:px-6 pb-14">
              {filteredNovels.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {filteredNovels.map((novel) => (
                    <Link
                      key={novel.id}
                      to={`/article/${novel.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex p-4 gap-4">
                        <div className="w-[90px] h-[126px] flex-shrink-0 rounded-md overflow-hidden">
                          <img
                            src={novel.coverImage}
                            alt={novel.title}
                            title={`${novel.title} - ${novel.category}`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            <h2 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                              {novel.title}
                            </h2>
                            <p className="text-xs text-foreground-500 mt-1">
                              <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                                <i className="ri-user-line text-[10px]"></i>
                              </span>
                              {novel.author}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {novel.tags.map((tag) => (
                                <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-700">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-3">
                            <span className="inline-flex items-center gap-0.5 text-xs text-accent-500">
                              <span className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-star-fill text-[10px]"></i>
                              </span>
                              {novel.rating}
                            </span>
                            <span className="text-xs text-foreground-400">{novel.wordCount}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}